import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { gameActions } from '@/lib/guide-structure';
import { WikiAnchorSection } from '@/components/wiki/WikiAnchorSection';
import { WikiToc } from '@/components/wiki/WikiToc';
import { GuideActionIcon } from '@/components/icons/GuideActionIcon';

export const Route = createFileRoute('/actions/$actionId')({
  component: ActionDetailPage,
});

function ActionDetailPage() {
  const { actionId } = Route.useParams();
  const action = gameActions.find(entry => entry.id === actionId);

  if (!action) {
    throw notFound();
  }

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'what-happens', label: 'What Happens' },
    { id: 'restrictions', label: 'Restrictions' },
    { id: 'factors', label: 'Outcome Factors' },
    { id: 'related-actions', label: 'Related Actions' },
  ];

  const related = gameActions.filter(entry => entry.category === action.category && entry.id !== action.id).slice(0, 6);

  return (
    <div className="space-y-5">
      <WikiToc items={toc} />

      <WikiAnchorSection id="overview" title={action.title}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge>{action.category}</Badge>
          <Badge>Action reference</Badge>
          <Badge>Formula-safe</Badge>
        </div>
        <div className="mt-3 grid gap-4 sm:grid-cols-[140px_1fr]">
          <GuideActionIcon
            action={action}
            size="medium"
            width={140}
            height={140}
            className="h-[140px] w-full max-w-[140px] overflow-hidden rounded-xl border border-[#d4af37]/30"
            imgClassName="h-[140px] w-full max-w-[140px] rounded-xl border border-[#d4af37]/30 object-cover"
          />
          <p>
            {action.summary} Think of this as your field guide: what this action is best for, when it gets blocked,
            and what usually decides whether it shines or fizzles.
          </p>
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="what-happens" title="What Happens">
        <ul className="list-disc space-y-1 pl-5">
          {action.whatHappens.map(step => (
            <li key={step}>{step}</li>
          ))}
        </ul>
      </WikiAnchorSection>

      <WikiAnchorSection id="restrictions" title="Restrictions">
        <ul className="list-disc space-y-1 pl-5">
          {action.restrictions.map(restriction => (
            <li key={restriction}>{restriction}</li>
          ))}
        </ul>
      </WikiAnchorSection>

      <WikiAnchorSection id="factors" title="Outcome Factors">
        <ul className="list-disc space-y-1 pl-5">
          {action.factors.map(factor => (
            <li key={factor}>{factor}</li>
          ))}
        </ul>
      </WikiAnchorSection>

      <WikiAnchorSection id="related-actions" title="Related Actions">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.map(entry => (
            <Link key={entry.id} to="/actions/$actionId" params={{ actionId: entry.id }}>
              <Card className="h-full p-3 transition-colors hover:border-[#d4af37]/40">
                <div className="flex items-start gap-3">
                  <GuideActionIcon
                    action={entry}
                    size="tiny"
                    width={40}
                    height={40}
                    className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-[#d4af37]/20"
                    imgClassName="h-10 w-10 shrink-0 rounded-lg border border-[#d4af37]/20 object-cover"
                  />
                  <div>
                    <h3 className="font-cinzel text-lg text-[#f0f0f0]">{entry.title}</h3>
                    <p className="text-sm text-[#b0b0b0]">{entry.summary}</p>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>
    </div>
  );
}
